import { fileExtension } from './file-format';

export type MeasureText = (text: string) => number;

const ELLIPSIS = '…';
const MAX_KEPT_EXTENSION_LENGTH = 10;
const MAX_STEM_END_LENGTH = 8;
const STEM_END_SHARE = 0.3;

export function splitAtExtension(name: string): { head: string; tail: string } {
  const extension = fileExtension(name);
  if (!extension || extension.length > MAX_KEPT_EXTENSION_LENGTH) return { head: name, tail: '' };
  const dot = name.lastIndexOf('.');
  return { head: name.slice(0, dot), tail: name.slice(dot) };
}

function longestFittingPrefix(
  stem: readonly string[],
  limit: number,
  fits: (prefix: string) => boolean,
): number {
  let low = 0;
  let high = limit;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (fits(stem.slice(0, mid).join(''))) low = mid;
    else high = mid - 1;
  }
  return low;
}

export function middleTruncate(name: string, maxWidth: number, measure: MeasureText): string {
  if (measure(name) <= maxWidth) return name;
  const { head, tail } = splitAtExtension(name);
  const stem = Array.from(head);
  if (stem.length === 0) return name;

  const fitsWith = (end: string) => (prefix: string) =>
    measure(`${prefix}${ELLIPSIS}${end}${tail}`) <= maxWidth;

  const visibleStem = longestFittingPrefix(stem, stem.length, fitsWith(''));
  let endLength = Math.min(MAX_STEM_END_LENGTH, Math.ceil(STEM_END_SHARE * visibleStem));
  if (endLength > 0 && !fitsWith(stem.slice(-endLength).join(''))('')) endLength = 0;
  const stemEnd = endLength > 0 ? stem.slice(-endLength).join('') : '';

  const headLength = longestFittingPrefix(stem, stem.length - endLength, fitsWith(stemEnd));
  return `${stem.slice(0, headLength).join('')}${ELLIPSIS}${stemEnd}${tail}`;
}

let measuringContext: OffscreenCanvasRenderingContext2D | null | undefined;

function sharedMeasuringContext(): OffscreenCanvasRenderingContext2D | null {
  if (measuringContext === undefined) {
    try {
      measuringContext =
        typeof OffscreenCanvas === 'function' ? new OffscreenCanvas(1, 1).getContext('2d') : null;
    } catch {
      measuringContext = null;
    }
  }
  return measuringContext;
}

export function textMeasurer(font: string): MeasureText | null {
  const context = sharedMeasuringContext();
  if (!context) return null;
  return (text) => {
    context.font = font;
    return context.measureText(text).width;
  };
}
